import React from "react";
import {
    Alert,
    Button,
    Card,
    Container,
    Form,
    FormControl,
} from "react-bootstrap";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router";

import { useAuth } from "../../contexts/auth_provider";
import urls from "../../urls.jsx";

const REDIRECT_TIME_SECONDS = 5;

function getResetLinkErrorMessage(error) {
    if (error.code === "auth/expired-action-code") {
        return "This reset link has expired. Request a new reset link below.";
    }

    if (error.code === "auth/invalid-action-code") {
        return "This reset link is invalid or has already been used. Request a new reset link below.";
    }

    return undefined;
}

function getPasswordRequirements(passwordValidation) {
    if (!passwordValidation) {
        return [];
    }

    const { customStrengthOptions } = passwordValidation.passwordPolicy;

    return [
        {
            label: `At least ${customStrengthOptions.minPasswordLength} characters`,
            meetsRequirement: passwordValidation.meetsMinPasswordLength,
        },
        {
            label: `At most ${customStrengthOptions.maxPasswordLength} characters`,
            meetsRequirement: passwordValidation.meetsMaxPasswordLength,
            isVisible: passwordValidation.meetsMaxPasswordLength === false,
        },
        {
            label: "Contains a lowercase letter",
            meetsRequirement: passwordValidation.containsLowercaseLetter,
        },
        {
            label: "Contains an uppercase letter",
            meetsRequirement: passwordValidation.containsUppercaseLetter,
        },
        {
            label: "Contains a number",
            meetsRequirement: passwordValidation.containsNumericCharacter,
        },
        {
            label: "Contains a symbol",
            meetsRequirement:
                passwordValidation.containsNonAlphanumericCharacter,
        },
    ].filter(
        ({ meetsRequirement, isVisible = true }) =>
            meetsRequirement !== undefined && isVisible,
    );
}

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = React.useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const oobCode = params.get("oobCode");
    const {
        resetPasswordLink,
        corvusVerifyPasswordResetCode,
        corvusConfirmPasswordReset,
        corvusValidatePassword,
    } = useAuth();
    const [email, setEmail] = React.useState("");
    const [resetEmail, setResetEmail] = React.useState();
    const [newPassword, setNewPassword] = React.useState("");
    const [confirmNewPassword, setConfirmNewPassword] = React.useState("");
    const [passwordValidation, setPasswordValidation] = React.useState();
    const [validatedPassword, setValidatedPassword] = React.useState();
    const [isValidatingPassword, setValidatingPassword] = React.useState(false);
    const [passwordValidationError, setPasswordValidationError] =
        React.useState();
    const [loading, setLoading] = React.useState(Boolean(oobCode));
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState();
    const [successMessage, setSuccessMessage] = React.useState();
    const [isPasswordResetComplete, setPasswordResetComplete] =
        React.useState(false);
    const [secondsLeft, setSecondsLeft] = React.useState(REDIRECT_TIME_SECONDS);

    React.useEffect(() => {
        let cancelled = false;

        if (!oobCode) {
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setResetEmail(undefined);
        setError(undefined);

        corvusVerifyPasswordResetCode(oobCode)
            .then((email) => {
                if (!cancelled) {
                    setResetEmail(email);
                    setEmail(email);
                }
            })
            .catch((verificationError) => {
                if (!cancelled) {
                    setError(
                        getResetLinkErrorMessage(verificationError) ??
                            "We could not verify this reset link. Request a new reset link below.",
                    );
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [oobCode, corvusVerifyPasswordResetCode]);

    React.useEffect(() => {
        if (!isPasswordResetComplete) {
            return undefined;
        }

        const interval = setInterval(() => {
            setSecondsLeft((seconds) => Math.max(seconds - 1, 0));
        }, 1000);
        const timeout = setTimeout(() => {
            navigate(urls.login());
        }, REDIRECT_TIME_SECONDS * 1000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [isPasswordResetComplete, navigate]);

    React.useEffect(() => {
        let cancelled = false;

        if (!resetEmail) {
            return undefined;
        }

        setValidatingPassword(true);

        corvusValidatePassword(newPassword)
            .then((status) => {
                if (!cancelled) {
                    setPasswordValidation(status);
                    setValidatedPassword(newPassword);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPasswordValidation(undefined);
                    setValidatedPassword(undefined);
                    setPasswordValidationError(
                        "We could not validate this password. Please try again.",
                    );
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setValidatingPassword(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [corvusValidatePassword, newPassword, resetEmail]);

    function doSendResetLink(event) {
        event.preventDefault();
        const resetEmail = email.trim();

        if (!resetEmail) {
            setError("Please enter an email address.");
            return;
        }

        const message = `If the email ${resetEmail} is linked to an account, we have sent a reset link.`;
        setError(undefined);
        setSubmitting(true);

        resetPasswordLink(resetEmail)
            .then(() => setSuccessMessage(message))
            .catch(() => setSuccessMessage(message))
            .finally(() => setSubmitting(false));
    }

    function doChangePassword(event) {
        event.preventDefault();

        if (!isPasswordValid || !passwordsMatch || isSubmitting) {
            setError(
                "Passwords do not match or the new password is not valid.",
            );
            return;
        }

        setError(undefined);
        setSubmitting(true);
        corvusConfirmPasswordReset(oobCode, newPassword)
            .then(() => {
                setSecondsLeft(REDIRECT_TIME_SECONDS);
                setPasswordResetComplete(true);
                setSuccessMessage("Password successfully changed.");
            })
            .catch((resetError) => {
                const resetLinkErrorMessage =
                    getResetLinkErrorMessage(resetError);

                if (resetLinkErrorMessage) {
                    setResetEmail(undefined);
                    setError(resetLinkErrorMessage);
                } else if (
                    resetError.code === "auth/weak-password" ||
                    resetError.code ===
                        "auth/password-does-not-meet-requirements"
                ) {
                    setError(
                        "Your password does not meet the current requirements. Please choose a different password.",
                    );
                } else {
                    setError(
                        "We could not reset your password. Please try again.",
                    );
                }
            })
            .finally(() => setSubmitting(false));
    }

    function updateNewPassword(event) {
        setNewPassword(event.target.value);
        setPasswordValidation(undefined);
        setValidatedPassword(undefined);
        setPasswordValidationError(undefined);
    }

    const isResetAction = Boolean(oobCode);
    const isVerifyingResetLink = isResetAction && loading;
    const showPasswordForm = Boolean(
        isResetAction && !loading && resetEmail && !successMessage,
    );
    const showRequestForm =
        (!isResetAction || (!loading && !resetEmail)) && !successMessage;
    const showLoginFooter = !isPasswordResetComplete;
    const isPasswordValid = Boolean(
        newPassword &&
            passwordValidation?.isValid &&
            validatedPassword === newPassword,
    );
    const passwordsMatch = Boolean(
        newPassword && newPassword === confirmNewPassword,
    );
    const passwordRequirements = getPasswordRequirements(passwordValidation);
    const passwordChecklistItems = [
        ...passwordRequirements,
        ...(confirmNewPassword
            ? [
                  {
                      label: passwordsMatch
                          ? "Passwords match"
                          : "Passwords do not match",
                      meetsRequirement: passwordsMatch,
                  },
              ]
            : []),
    ];

    return (
        <>
            <div className="my-5 text-center">
                <img
                    style={{ maxWidth: "300px", maxHeight: "100px" }}
                    src="/MyCorvus.png"
                    alt="none"
                />
            </div>

            <Container style={{ maxWidth: "400px" }}>
                <title>Reset Password | MyCorvus</title>

                {error && <Alert variant="danger">{error}</Alert>}
                {successMessage && (
                    <Card>
                        <Card.Body>
                            <Alert variant="success" className="mb-0">
                                <FaCheckCircle className="me-2" />
                                {successMessage}
                            </Alert>

                            {isPasswordResetComplete && (
                                <>
                                    <p className="mb-2 mt-3">
                                        {
                                            "We'll take you to the login page shortly."
                                        }
                                    </p>

                                    <small className="text-muted d-block mb-3">
                                        Going to the login page in {secondsLeft}{" "}
                                        seconds…
                                    </small>

                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => navigate(urls.login())}
                                    >
                                        Go to the login page now
                                    </Button>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {isVerifyingResetLink && (
                    <Alert variant="info">Verifying reset link...</Alert>
                )}

                {showPasswordForm && (
                    <Card>
                        <Card.Body>
                            <Alert variant="info">
                                Resetting password for <b>{resetEmail}</b>
                            </Alert>

                            <Form onSubmit={doChangePassword}>
                                {isValidatingPassword && (
                                    <Form.Text className="d-block mb-2 text-muted">
                                        Checking password requirements...
                                    </Form.Text>
                                )}

                                {passwordChecklistItems.length > 0 && (
                                    <ul className="list-unstyled mb-2">
                                        {passwordChecklistItems.map(
                                            ({ label, meetsRequirement }) => (
                                                <li
                                                    className={
                                                        meetsRequirement
                                                            ? "text-success"
                                                            : "text-danger"
                                                    }
                                                    key={label}
                                                >
                                                    {meetsRequirement ? (
                                                        <FaCheckCircle className="me-2" />
                                                    ) : (
                                                        <FaTimesCircle className="me-2" />
                                                    )}
                                                    {label}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}

                                {passwordValidationError && (
                                    <Form.Text className="d-block mb-2 text-danger">
                                        {passwordValidationError}
                                    </Form.Text>
                                )}

                                <Form.Group>
                                    <Form.Label>
                                        <b>New Password</b>
                                    </Form.Label>
                                    <FormControl
                                        type="password"
                                        value={newPassword}
                                        onChange={updateNewPassword}
                                    />
                                </Form.Group>

                                <Form.Group className="mt-2">
                                    <Form.Label>
                                        <b>Confirm New Password</b>
                                    </Form.Label>
                                    <FormControl
                                        type="password"
                                        value={confirmNewPassword}
                                        onChange={(e) =>
                                            setConfirmNewPassword(
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Form.Group>

                                <Button
                                    type="submit"
                                    className="w-100 mt-3"
                                    disabled={
                                        isSubmitting ||
                                        isValidatingPassword ||
                                        !isPasswordValid ||
                                        !passwordsMatch
                                    }
                                >
                                    <b>
                                        {isSubmitting
                                            ? "Changing password..."
                                            : "Change password"}
                                    </b>
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                )}

                {showRequestForm && (
                    <Card>
                        <Card.Body>
                            <Form onSubmit={doSendResetLink}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        <b>Email address</b>
                                    </Form.Label>
                                    <FormControl
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                    />
                                </Form.Group>

                                <div className="d-grid gap-2">
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        <b>
                                            {isSubmitting
                                                ? "Sending..."
                                                : "Send reset link"}
                                        </b>
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                )}
            </Container>

            {showLoginFooter && (
                <div className="text-center mt-3">
                    Have a login? <Link to={urls.login()}>Login</Link>
                </div>
            )}
        </>
    );
}
